// Pure rules and copy for the Classrooms rollout card, kept out of the page so
// they are unit-tested on their own (rollout.test.ts).
//
// isClassroomOpen() MIRRORS public.classroom_open(p_year) in
// 20261016120000_classrooms_gate.sql:
//   mode = 'everyone' or (mode = 'pilot' and btrim(p_year) = any(pilot_years))
// Change them together. The dashboard only DISPLAYS this; the server decides.
//
// upstreamBlock() MIRRORS the flag half of _classroom_visible_to
// (20261018120000_groups_audience_core.sql): every Classroom is closed,
// admins included, while groups_enabled or groups_class_chapters_enabled
// reads false. groupsAudience (groups_rollout.mode) never blocks — it only
// changes who, among non-admins, can preview an already-open mode.

import type { ClassroomMode, ClassroomSettings, ClassroomUpstream, GroupsAudienceMode } from './useClassroomSettings'

/** Narrowest to widest. Each step shows Classrooms to more people. */
export const CLASSROOM_MODES: ClassroomMode[] = ['off', 'admins', 'pilot', 'everyone']

export const MODE_LABELS: Record<ClassroomMode, string> = {
  off: 'Off',
  admins: 'Admins & testers',
  pilot: 'Pilot',
  everyone: 'Everyone',
}

// MODE_HELP is a static Record with no upstream reading of its own (unlike
// modeConfirm() below, which gets `groupsAudience` at the moment of a real
// confirm) — so its tester sentences NAME the condition ("while Groups is
// set to Admins + testers or Everyone", classroomsStatusPhrase's own
// wording) instead of asserting testers can see their class as verified
// fact. Asserting it unconditionally was the GS-5 defect this page must not
// reintroduce: the rollout is seeded to 'admins', where testers are parked
// (`_groups_tester_active`, 20261018120000_groups_audience_core.sql).
export const MODE_HELP: Record<ClassroomMode, string> = {
  off: 'No Classroom is open to members, and nobody gets Classroom entry points. Admins can still reach every Classroom from Alumni › Groups.',
  admins:
    "Admins see every Classroom. Groups testers (Group Settings) see only their own class's Classroom while Groups is set to Admins + testers or Everyone. Members see nothing.",
  pilot:
    "Members of the pilot classes below see their Classroom, and Groups testers see their own class's while Groups is set to Admins + testers or Everyone. Other members get no Classroom, but memories posted in an open Classroom show in every member's Gallery.",
  everyone:
    "Every member sees their own class's Classroom. To close one class, list every other class under Pilot class years, then switch to Pilot.",
}

/** True when `to` shows Classrooms to more people than `from`. Widening asks first; narrowing never does. */
export function isWidening(from: ClassroomMode, to: ClassroomMode): boolean {
  return CLASSROOM_MODES.indexOf(to) > CLASSROOM_MODES.indexOf(from)
}

/** Trimmed, four-digit, de-duplicated, ascending — what classroom_years_valid() accepts. */
export function normalizeYears(years: readonly string[]): string[] {
  return Array.from(new Set(years.map((y) => y.trim()).filter((y) => /^\d{4}$/.test(y)))).sort()
}

/** The years in `next` that `prev` did not have. Adding a year widens; removing one narrows. */
export function addedYears(prev: readonly string[], next: readonly string[]): string[] {
  const had = new Set(normalizeYears(prev))
  return normalizeYears(next).filter((y) => !had.has(y))
}

/** Mirrors public.classroom_open(p_year). */
export function isClassroomOpen(settings: ClassroomSettings, classYear: string): boolean {
  return settings.mode === 'everyone' || (settings.mode === 'pilot' && settings.pilot_years.includes(classYear.trim()))
}

export type OpenState = 'open' | 'closed' | 'inactive' | 'unknown'

/** groups_off (Groups is off) · chapters_off (Class chapters is off) · unknown (couldn't read) · null (nothing blocks). */
export type UpstreamBlock = 'groups_off' | 'chapters_off' | 'unknown' | null

/**
 * Whether `upstream` (Group Settings' switches) keeps every Classroom closed
 * before `settings.mode` even gets a say — mirrors the flag half of
 * _classroom_visible_to. `u` is null/undefined when the caller has no
 * upstream reading at all (e.g. a one-argument call site kept for
 * backward compatibility) — that is never treated as a block.
 */
export function upstreamBlock(u: ClassroomUpstream | null | undefined): UpstreamBlock {
  if (u == null) return null
  if (u.groupsOn === false) return 'groups_off'
  if (u.chaptersOn === false) return 'chapters_off'
  if (u.groupsOn === null || u.chaptersOn === null) return 'unknown'
  return null
}

/**
 * Whether members of this class can open this Classroom right now. 'unknown'
 * when the settings row, or the upstream switches, could not be read — never
 * 'closed', which would be a guess. A chapter that is not active
 * (suspended/archived) is closed to members whatever the mode, because
 * can_see_group() requires status = 'active'.
 */
export function classroomOpenState(
  settings: ClassroomSettings | null,
  classYear: string,
  status: string,
  upstream?: ClassroomUpstream | null,
): OpenState {
  if (!settings) return 'unknown'
  if (status !== 'active') return 'inactive'
  const block = upstreamBlock(upstream)
  if (block === 'unknown') return 'unknown'
  if (block === 'groups_off' || block === 'chapters_off') return 'closed'
  return isClassroomOpen(settings, classYear) ? 'open' : 'closed'
}

/** "the Class of 2016" · "the Classes of 1996 and 2016" · "the Classes of 1996, 2001 and 2016". */
export function classesPhrase(years: readonly string[]): string {
  const ys = normalizeYears(years)
  if (ys.length === 0) return 'no class'
  if (ys.length === 1) return `the Class of ${ys[0]}`
  return `the Classes of ${ys.slice(0, -1).join(', ')} and ${ys[ys.length - 1]}`
}

/**
 * A phone that hasn't caught up to the Classrooms app update still reads the
 * old, readable groups_enabled row and shows the whole old Groups tab
 * instead of a Classroom (docs/classrooms/ROLLOUT.md, Risks) — every "opens
 * right away" claim needs this caveat.
 */
export const OLD_APP_WARNING =
  "A phone that hasn't installed the Classrooms app update shows the older Groups tab instead of the Classroom, so check the update's reach first (ROLLOUT.md, Risks)."

export function openNowText(settings: ClassroomSettings, upstream?: ClassroomUpstream | null): string {
  const block = upstreamBlock(upstream)
  if (block === 'groups_off') {
    return 'Open now: nothing. Groups is off in Group Settings (Who can use Groups: Off), so every Classroom is closed, admins included.'
  }
  if (block === 'chapters_off') {
    return 'Open now: nothing. Class chapters is off in Group Settings, so every Classroom is closed, admins included.'
  }
  // Both branches below build `base` first and let the shared block==='unknown'
  // caveat apply afterward — the parked-testers sentence must not skip it just
  // because it also depends on upstream (groupsAudience).
  let base: string
  if (settings.mode === 'admins' && upstream?.groupsAudience === 'admins') {
    base = 'Open now: admins only. Groups testers are parked while Who can use Groups is Admins only. Members see no Classroom.'
  } else {
    switch (settings.mode) {
      case 'off':
        base = 'Open now: no class. Classrooms are closed to members.'
        break
      case 'admins':
        base = 'Open now: admins, and testers for their own class. Members see no Classroom.'
        break
      case 'pilot':
        base = normalizeYears(settings.pilot_years).length
          ? `Open now: ${classesPhrase(settings.pilot_years)}.`
          : 'Open now: no class. The pilot has no class years yet.'
        break
      case 'everyone':
        base = 'Open now: every class.'
        break
    }
  }
  if (block === 'unknown') {
    return base + " Couldn't read the Groups switches in Group Settings, so this may overstate what is open."
  }
  if (settings.mode === 'admins' && upstream != null && upstream.groupsAudience === null) {
    return base + " Couldn't read Who can use Groups in Group Settings, so Groups testers' access is unknown."
  }
  return base
}

export interface ConfirmCopy {
  title: string
  content: string
  okText: string
}

/**
 * The sentence(s) said before opening `years` — split into three groups:
 * years that actually open (an active "Class of YYYY" chapter exists),
 * years whose chapter exists but is not active (suspended/archived, so
 * can_see_group() still hides it), and years with no chapter row at all yet
 * (added to Pilot, or named in a switch to Pilot, before the chapter has
 * been created). Both `withoutClassroom` and `inactive` are the caller's
 * concern (usually `yearsWithoutClassroom`/`yearsInactive` on the years
 * being opened) — this function only partitions `years` by them, it does
 * not recompute either list.
 */
function openingCopy(years: readonly string[], withoutClassroom: readonly string[], inactive: readonly string[] = []): string {
  const ys = normalizeYears(years)
  const withoutSet = new Set(normalizeYears(withoutClassroom))
  const inactiveSet = new Set(normalizeYears(inactive))
  const missing = ys.filter((y) => withoutSet.has(y))
  const notActive = ys.filter((y) => inactiveSet.has(y) && !withoutSet.has(y))
  const present = ys.filter((y) => !withoutSet.has(y) && !inactiveSet.has(y))
  const parts: string[] = []
  if (present.length > 0) {
    parts.push(`Members of ${classesPhrase(present)} will see their Classroom the next time they open the app. ${OLD_APP_WARNING}`)
  }
  if (notActive.length > 0) {
    const phrase = classesPhrase(notActive)
    const capitalized = phrase.charAt(0).toUpperCase() + phrase.slice(1)
    const isAre = notActive.length === 1 ? 'is' : 'are'
    const classroomWord = notActive.length === 1 ? 'Classroom' : 'Classrooms'
    const itThem = notActive.length === 1 ? 'it' : 'them'
    parts.push(`${capitalized}’s ${classroomWord} ${isAre} not active, so nobody sees ${itThem}.`)
  }
  if (missing.length > 0) {
    const phrase = classesPhrase(missing)
    const capitalized = phrase.charAt(0).toUpperCase() + phrase.slice(1)
    const have = missing.length === 1 ? 'has' : 'have'
    const itThem = missing.length === 1 ? 'it' : 'them'
    parts.push(`${capitalized} ${have} no Classroom yet, so nobody sees anything for ${itThem} until one exists.`)
  }
  return parts.join(' ')
}

/** Appended to a widening confirm's content when `block` shows Group Settings itself would still keep the result closed. */
function upstreamBlockSuffix(block: UpstreamBlock | undefined): string {
  return block === 'groups_off' || block === 'chapters_off'
    ? ' None of this opens until Groups and Class chapters are on in Group Settings.'
    : ''
}

/**
 * The question asked before widening to `to`. Only called when isWidening()
 * is true. `withoutClassroom` (default []) names the years in `pilotYears`
 * that have no "Class of YYYY" chapter yet (yearsWithoutClassroom), and
 * `inactiveYears` (default []) names the ones whose chapter exists but is
 * not active (yearsInactive) — both used only by the 'pilot' case. `block`
 * (default undefined, meaning "not currently known to be blocked") is
 * upstreamBlock(upstream): when it is 'groups_off' or 'chapters_off', every
 * "will see their Classroom" sentence gets an upstream caveat appended,
 * since Group Settings still keeps the result closed either way.
 * `groupsAudience` (default undefined, meaning "not currently known") is
 * upstream.groupsAudience (groups_rollout.mode) — used only by the 'admins'
 * case, and only to say something MORE precise than MODE_HELP.admins can:
 * when it is 'admins' (testers parked, `_groups_tester_active` requires
 * `testers`/`everyone`), the confirm says so outright, the way
 * openNowText()'s parked sentence does, instead of the false "Groups
 * testers see their own class's Classroom" claim this replaced (GS-5).
 * Anything else (including undefined/null, an unread "Who can use Groups")
 * NAMES the condition rather than asserting it, same as MODE_HELP.admins.
 */
export function modeConfirm(
  to: ClassroomMode,
  pilotYears: readonly string[],
  withoutClassroom: readonly string[] = [],
  inactiveYears: readonly string[] = [],
  block?: UpstreamBlock,
  groupsAudience?: GroupsAudienceMode | null,
): ConfirmCopy {
  const suffix = upstreamBlockSuffix(block)
  switch (to) {
    case 'admins': {
      const testersSentence =
        groupsAudience === 'admins'
          ? "Admins with a class year get their Classroom's entry points on Home and Alumni. Groups testers are parked while Who can use Groups is Admins only."
          : "Admins with a class year get their Classroom's entry points on Home and Alumni, and Groups testers (Group Settings) see their own class's Classroom while Groups is set to Admins + testers or Everyone."
      return {
        title: 'Show Classrooms to admins?',
        content: `${testersSentence} Other members still see nothing.${suffix}`,
        okText: 'Show to admins',
      }
    }
    case 'pilot':
      return {
        title: 'Open the pilot Classrooms?',
        content: normalizeYears(pilotYears).length
          ? openingCopy(pilotYears, withoutClassroom, inactiveYears) + suffix
          : 'No class is in the pilot yet, so members will see nothing until you add a class year.',
        okText: 'Open the pilot',
      }
    case 'everyone':
      return {
        title: 'Open every Classroom?',
        content: `Every member with a class year will see their class's Classroom the next time they open the app. ${OLD_APP_WARNING}${suffix}`,
        okText: 'Open to everyone',
      }
    case 'off':
      return { title: 'Close every Classroom?', content: 'Members will stop seeing Classrooms right away.', okText: 'Close' }
  }
}

/**
 * The question asked before adding pilot years. `withoutClassroom` and
 * `inactiveYears` (both default []) and `block` (default undefined) — see
 * modeConfirm.
 */
export function yearsConfirm(
  added: readonly string[],
  withoutClassroom: readonly string[] = [],
  inactiveYears: readonly string[] = [],
  block?: UpstreamBlock,
): ConfirmCopy {
  const who = classesPhrase(added)
  return {
    title: `Add ${who} to the pilot?`,
    content: openingCopy(added, withoutClassroom, inactiveYears) + upstreamBlockSuffix(block),
    okText: 'Add to the pilot',
  }
}

/**
 * The label shown for a pilot-year option/tag: plain when its "Class of
 * YYYY" chapter exists and is active, annotated otherwise so an admin never
 * mistakes "in the pilot" for "members can actually see it". A null map
 * (chapters not yet known — the roster load hasn't completed) never
 * annotates, since we don't know either way.
 */
export function pilotYearLabel(year: string, chapterStatus: ReadonlyMap<string, string> | null): string {
  if (chapterStatus === null) return year
  const status = chapterStatus.get(year.trim())
  if (status === undefined) return `${year} (no Classroom yet)`
  if (status !== 'active') return `${year} (Classroom not active)`
  return year
}

/** The normalized `years` that have no chapter entry in `chapterStatus` (null map → none, since chapters aren't known). An inactive chapter still counts as present — see yearsInactive for that case. */
export function yearsWithoutClassroom(years: readonly string[], chapterStatus: ReadonlyMap<string, string> | null): string[] {
  if (chapterStatus === null) return []
  return normalizeYears(years).filter((y) => !chapterStatus.has(y))
}

/**
 * The normalized `years` whose chapter DOES exist in `chapterStatus` but is
 * not active (suspended/archived). Disjoint from yearsWithoutClassroom: a
 * year missing a chapter row entirely is "without a Classroom", not
 * "inactive" — both are closed to members, but for different reasons and
 * with different fixes (create the chapter vs. reactivate it).
 */
export function yearsInactive(years: readonly string[], chapterStatus: ReadonlyMap<string, string> | null): string[] {
  if (chapterStatus === null) return []
  return normalizeYears(years).filter((y) => {
    const status = chapterStatus.get(y)
    return status !== undefined && status !== 'active'
  })
}
